import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RecruitGroupResponseDto, RecruitGroupTypesEnum } from '@/app/common/general-types/recruit-groups.dto';
import {
  CREATOR_REVIEW_FILTERS,
  CreatorReviewFilter,
  CreatorReviewStatus,
  creatorReviewFilterCounts,
  creatorReviewFilterEmptyCopy,
  parseRecruitGroupReview,
  resolveCreatorReviewStatus,
  visibleGroupDescription,
} from '@/app/common/general-types/recruit-groups.review';
import { GROUP_LOCKED_MESSAGE, RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { distinctUntilChanged, firstValueFrom, map, Subscription, take } from 'rxjs';
import { CreateGroup } from './atoms/create-group/create-group';
import { EditGroup } from './atoms/edit-group/edit-group';
import { ShareGroup } from './atoms/share-group/share-group';
import { GroupsWhatsNew } from './atoms/groups-whats-new/groups-whats-new';
import { GroupsWhatsNewService } from './services/groups-whats-new/groups-whats-new.service';
import { CreatorService } from '@/app/services/creator/creator.service';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { GrouCreatorCard } from './atoms/grou-creator-card/grou-creator-card';
import { CollaboratorService } from '@/app/services/collaborator/collaborator.service';
import { CollaboratorsComponent } from '@/app/pages/creators/molecules/collaborators/collaborators.component';
import { CreatorProfileComponent } from '@/app/pages/creators/molecules/creator-profile/creator-profile.component';
import { MagicSearchService } from '@/app/pages/creators/service/magic-search/magic-search.service';
import { TooltipDirective } from '@/app/common/directives/tooltip/tooltip.directive';
import { ToastNotificationOptions, ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { DeletingOverlayComponent } from '@/app/atoms/deleting-overlay/deleting-overlay.component';

@Component({
  selector: 'app-creators-my-groups',
  imports: [CreateGroup, EditGroup, ShareGroup, GroupsWhatsNew, GrouCreatorCard, CollaboratorsComponent, CreatorProfileComponent, TooltipDirective, DeletingOverlayComponent, RouterLink],
  templateUrl: './creators-my-groups.html',
  styleUrl: './creators-my-groups.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorsMyGroups implements OnInit, OnDestroy {
  @ViewChild('inviteSelectedTarget') private inviteSelectedTarget?: ElementRef<HTMLElement>;

  loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  deleteGroupState = signal<'idle' | 'deleting' | 'deleted' | 'error'>('idle');
  selectedGroup = signal<RecruitGroupResponseDto | null>(null);

  showCreateGroupModal = signal<boolean>(false);
  showEditGroupModal = signal<boolean>(false);
  showShareGroupModal = signal<boolean>(false);
  selectedGroupCreators = signal<CreatorResponseDto[] | undefined>(undefined);
  readonly reviewFilter = signal<CreatorReviewFilter>('all');
  readonly inviteTargetAttention = signal(false);

  private location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  public currentUserService = inject(CurrentUserService);
  private recruitGroupService = inject(RecruitGroupService);
  private creatorService = inject(CreatorService);
  private suppressCreatorUrlSync = true;
  public collaboratorsService = inject(CollaboratorService);
  public magicSearchService = inject(MagicSearchService);
  public whatsNewService = inject(GroupsWhatsNewService);
  private toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);
  private activeFlyDotAnimation: Animation | null = null;
  private activeFlyDotElement: HTMLElement | null = null;
  private attentionResetTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastHandledSelectionGuideNonce = 0;
  private readonly subscriptionList = new Subscription();

  readonly groupList = computed(() => {
    const merged = [
      ...(this.recruitGroupService.allRecruitGroups() ?? []),
      ...(this.recruitGroupService.myRecruitGroups() ?? []),
      ...(this.recruitGroupService.brandRecruitGroups() ?? []),
    ];
    const seen = new Set<string>();
    const groups: RecruitGroupResponseDto[] = [];
    for (const group of merged) {
      if (seen.has(group.id)) {
        continue;
      }
      seen.add(group.id);
      groups.push(group);
    }
    return groups.sort((left, right) => groupAddedAt(right) - groupAddedAt(left));
  });

  readonly reviewFilterTabs = computed(() => {
    const creators = this.selectedGroupCreators() ?? [];
    const counts = creatorReviewFilterCounts(
      creators.map((creator) => this.creatorReviewStatus(creator)),
    );
    return CREATOR_REVIEW_FILTERS.map((tab) => ({
      ...tab,
      count: counts[tab.id],
    }));
  });

  readonly filteredSelectedGroupCreators = computed(() => {
    const creators = this.selectedGroupCreators() ?? [];
    const filter = this.reviewFilter();
    if (filter === 'all') {
      return creators;
    }
    return creators.filter((creator) => this.creatorReviewStatus(creator) === filter);
  });

  readonly emptyFilterCopy = computed(() => creatorReviewFilterEmptyCopy(this.reviewFilter()));

  constructor() {
    effect(() => {
      const creatorId = this.magicSearchService.portfolioCreatorId();
      if (this.suppressCreatorUrlSync) {
        return;
      }
      this.syncCreatorIdToUrl(creatorId);
    });

    effect(() => {
      const guide = this.collaboratorsService.selectionGuide();
      if (!guide || guide.nonce === this.lastHandledSelectionGuideNonce) {
        return;
      }
      this.lastHandledSelectionGuideNonce = guide.nonce;
      this.runSelectionGuideAnimation(guide.originX, guide.originY);
    });
  }

  async ngOnInit(): Promise<void> {
    this.subscriptionList.add(
      this.route.queryParamMap.pipe(
        map((params) => params.get('group')),
        distinctUntilChanged(),
      ).subscribe((groupQuery) => {
        this.openRequestedGroup(groupQuery);
      }),
    );
    this.selectFirstGroupIfNoneSelected();
    await this.recruitGroupService.initRecruitmentGroups();
    this.openRequestedGroup();
    this.selectFirstGroupIfNoneSelected();

    const creatorIdFromQuery = this.route.snapshot.queryParamMap.get('creatorId') ?? undefined;
    if (creatorIdFromQuery) {
      this.openCreatorProfileFromUrl(creatorIdFromQuery);
    } else {
      this.suppressCreatorUrlSync = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
    this.clearActiveFlyDot();
    if (this.attentionResetTimeoutId !== null) {
      clearTimeout(this.attentionResetTimeoutId);
      this.attentionResetTimeoutId = null;
    }
  }

  openRequestedGroup(
    groupQuery = this.route.snapshot.queryParamMap.get('group'),
  ): void {
    if (!groupQuery) {
      return;
    }

    if (groupQuery === RecruitGroupTypesEnum.CREATOR_INTEREST) {
      const interestGroup = this.groupList().find((group) => this.isShoutGroup(group));
      if (interestGroup) {
        this.selectGroup(interestGroup);
      }
      return;
    }

    const requestedGroup = this.groupList().find((group) => group.id === groupQuery);
    if (requestedGroup) {
      this.selectGroup(requestedGroup);
    }
  }

  private openCreatorProfileFromUrl(creatorId: string): void {
    this.subscriptionList.add(
      this.creatorService.getCreatorById(creatorId).pipe(take(1)).subscribe({
        next: (creator) => {
          const navItems = (this.selectedGroupCreators() ?? []).map((item) => ({ creator: item }));
          this.magicSearchService.openCreatorPortfolio(creator, { navItems });
          this.suppressCreatorUrlSync = false;
        },
        error: (err) => {
          console.warn('Unable to open creator profile from URL: ', err);
          this.suppressCreatorUrlSync = false;
          this.syncCreatorIdToUrl(undefined);
        },
      }),
    );
  }

  private syncCreatorIdToUrl(creatorId: string | undefined): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    const currentCreatorId = queryParams['creatorId'] as string | undefined;
    if ((currentCreatorId ?? undefined) === (creatorId ?? undefined)) {
      return;
    }

    if (creatorId) {
      queryParams['creatorId'] = creatorId;
    } else {
      delete queryParams['creatorId'];
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/creators-my-groups'], { queryParams }),
    );
    this.location.replaceState(url);
  }

  selectFirstGroupIfNoneSelected(): void {
    if (this.selectedGroup()) {
      return;
    }

    const firstGroup = this.groupList()[0];
    if (firstGroup) {
      this.selectGroup(firstGroup);
    }
  }

  isTeamVisible(group: RecruitGroupResponseDto): boolean {
    return Boolean(group.isGlobal || group.businessAccountId?.trim() || group.agencyAccountId?.trim());
  }

  visibilityLabel(group: RecruitGroupResponseDto): string {
    return this.isTeamVisible(group) ? 'Visible to team' : 'Visible to me';
  }

  visibilityPillClass(group: RecruitGroupResponseDto): string {
    return this.isTeamVisible(group)
      ? 'bg-primary/10 text-accent'
      : 'bg-pink-500/10 text-pink-600 dark:text-pink-400';
  }

  folderIconClass(group: RecruitGroupResponseDto): string {
    return this.isTeamVisible(group) ? 'bg-primary' : 'bg-pink-600';
  }

  creatorCountLabel(group: RecruitGroupResponseDto): string {
    const count = group.numberOfCreators ?? 0;
    return count === 1 ? '1 creator' : `${count} creators`;
  }

  isShoutGroup(group: RecruitGroupResponseDto): boolean {
    return group.group_type === RecruitGroupTypesEnum.CREATOR_INTEREST;
  }

  groupVisibleDescription(group: RecruitGroupResponseDto | null | undefined): string {
    return visibleGroupDescription(group?.groupDescription);
  }

  groupApprovedByName(group: RecruitGroupResponseDto | null | undefined): string | undefined {
    return group ? parseRecruitGroupReview(group).approvedByName?.trim() : undefined;
  }

  groupHasCreatorNotes(group: RecruitGroupResponseDto | null | undefined): boolean {
    return group ? Object.keys(parseRecruitGroupReview(group).creatorNotes).length > 0 : false;
  }

  creatorNote(creator: CreatorResponseDto): string | undefined {
    const group = this.selectedGroup();
    if (!group) {
      return undefined;
    }
    return parseRecruitGroupReview(group).creatorNotes[creator.blazeFirebaseUid];
  }

  isDeclined(creator: CreatorResponseDto): boolean {
    const group = this.selectedGroup();
    if (!group) {
      return false;
    }
    return parseRecruitGroupReview(group).droppedCreatorIds.includes(creator.blazeFirebaseUid);
  }

  isApproved(creator: CreatorResponseDto): boolean {
    const group = this.selectedGroup();
    if (!group) {
      return false;
    }
    const review = parseRecruitGroupReview(group);
    return review.keptCreatorIds.includes(creator.blazeFirebaseUid)
      && !review.droppedCreatorIds.includes(creator.blazeFirebaseUid);
  }

  isInvited(creator: CreatorResponseDto): boolean {
    const group = this.selectedGroup();
    if (!group) {
      return false;
    }
    return (parseRecruitGroupReview(group).invitedCreatorIds ?? []).includes(creator.blazeFirebaseUid);
  }

  creatorReviewStatus(creator: CreatorResponseDto): CreatorReviewStatus {
    const group = this.selectedGroup();
    return resolveCreatorReviewStatus({
      declined: this.isDeclined(creator),
      approved: this.isApproved(creator),
      hasNote: Boolean(this.creatorNote(creator)),
      groupApproved: group?.approvalStatus === 'approved',
    });
  }

  setReviewFilter(filter: CreatorReviewFilter): void {
    this.reviewFilter.set(filter);
  }

  isReviewFilterSelected(filter: CreatorReviewFilter): boolean {
    return this.reviewFilter() === filter;
  }

  reviewFilterButtonClass(filter: CreatorReviewFilter): string {
    const selected = this.isReviewFilterSelected(filter);
    const base = 'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';
    if (!selected) {
      return `${base} bg-zinc-100 text-secondary dark:bg-zinc-800`;
    }
    if (filter === 'pending') {
      return `${base} bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800`;
    }
    if (filter === 'approved') {
      return `${base} bg-green-100 text-green-800 ring-1 ring-inset ring-green-200 dark:bg-green-900/30 dark:text-green-200 dark:ring-green-800`;
    }
    if (filter === 'declined') {
      return `${base} bg-rose-100 text-rose-800 ring-1 ring-inset ring-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:ring-rose-800`;
    }
    if (filter === 'feedback') {
      return `${base} bg-sky-100 text-sky-800 ring-1 ring-inset ring-sky-200 dark:bg-sky-900/30 dark:text-sky-200 dark:ring-sky-800`;
    }
    return `${base} bg-zinc-100 text-primary ring-1 ring-inset ring-zinc-300 dark:bg-zinc-800 dark:ring-zinc-600`;
  }

  reviewLabel(creator: CreatorResponseDto): string | undefined {
    const group = this.selectedGroup();
    if (!group) {
      return undefined;
    }
    const review = parseRecruitGroupReview(group);
    if (this.isDeclined(creator)) {
      const name = review.droppedByName?.trim();
      return name ? `Declined by ${name}` : 'Declined';
    }
    if (this.isApproved(creator)) {
      const name = review.keptByName?.trim();
      return name ? `Approved by ${name}` : 'Approved';
    }
    if (this.creatorNote(creator)) {
      const name = review.droppedByName?.trim()
        || review.keptByName?.trim()
        || review.approvedByName?.trim();
      return name ? `Feedback from ${name}` : 'Feedback';
    }
    if (group.approvalStatus === 'approved') {
      const name = review.approvedByName?.trim();
      return name ? `Approved by ${name}` : 'Approved';
    }
    return undefined;
  }

  selectGroup(group: RecruitGroupResponseDto) {
    console.log('selectGroup()', group);
    this.reviewFilter.set('all');
    this.selectedGroupCreators.set(undefined);
    this.selectedGroup.set(group);
    this.creatorService.getCreatorsByRecruitmentGroupId(group.id).pipe(take(1)).subscribe((creators) => {
      console.log('Creators', creators);
      this.selectedGroupCreators.set(creators);
    }, (error) => {
      console.error('Error getting creators', error);
      this.selectedGroupCreators.set(undefined);
    });
  }

  backToCreators() {
    this.location.back();
  }

  closeCreateGroup(created?: RecruitGroupResponseDto) {
    this.showCreateGroupModal.set(false);
    if (created) {
      void this.recruitGroupService.initRecruitmentGroups();
    }
  }

  openShareGroup(event: MouseEvent): void {
    event.stopPropagation();
    this.showShareGroupModal.set(true);
  }

  openInviteSelectedCreators(event: MouseEvent): void {
    event.stopPropagation();
    if (this.collaboratorsService.selectedCreators().length === 0) {
      return;
    }
    this.collaboratorsService.showCollaborators.set(true);
  }

  private runSelectionGuideAnimation(originX: number, originY: number): void {
    const targetHost = this.inviteSelectedTarget?.nativeElement;
    if (!targetHost) {
      this.triggerInviteTargetAttention();
      return;
    }

    const targetRect = targetHost.getBoundingClientRect();
    const targetX = targetRect.left + targetRect.width / 2;
    const targetY = targetRect.top + targetRect.height / 2;

    if (this.prefersReducedMotion()) {
      this.triggerInviteTargetAttention();
      return;
    }

    this.clearActiveFlyDot();

    const dot = document.createElement('span');
    dot.setAttribute('aria-hidden', 'true');
    dot.className = 'collaborators-fly-dot';
    dot.style.position = 'fixed';
    dot.style.zIndex = '1000';
    dot.style.width = '1.5rem';
    dot.style.height = '1.5rem';
    dot.style.borderRadius = '9999px';
    dot.style.backgroundColor = 'var(--primary)';
    dot.style.boxShadow = '0 0 0 8px color-mix(in srgb, var(--primary) 25%, transparent)';
    dot.style.pointerEvents = 'none';
    dot.style.transform = 'translate(-50%, -50%)';
    dot.style.left = `${originX}px`;
    dot.style.top = `${originY}px`;
    document.body.appendChild(dot);
    this.activeFlyDotElement = dot;

    const deltaX = targetX - originX;
    const deltaY = targetY - originY;

    this.activeFlyDotAnimation = dot.animate(
      [
        { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
        { transform: `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px)) scale(0.6)`, opacity: 0.85 },
      ],
      {
        duration: 650,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      },
    );

    this.activeFlyDotAnimation.onfinish = () => {
      this.clearActiveFlyDot();
      this.triggerInviteTargetAttention();
    };
    this.activeFlyDotAnimation.oncancel = () => {
      this.clearActiveFlyDot();
    };
  }

  private triggerInviteTargetAttention(): void {
    this.inviteTargetAttention.set(false);

    queueMicrotask(() => {
      this.inviteTargetAttention.set(true);
    });

    if (this.attentionResetTimeoutId !== null) {
      clearTimeout(this.attentionResetTimeoutId);
    }

    this.attentionResetTimeoutId = setTimeout(() => {
      this.inviteTargetAttention.set(false);
      this.attentionResetTimeoutId = null;
    }, 620);
  }

  private clearActiveFlyDot(): void {
    this.activeFlyDotAnimation?.cancel();
    this.activeFlyDotAnimation = null;
    this.activeFlyDotElement?.remove();
    this.activeFlyDotElement = null;
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** True once the group has a Noodle list: membership is add-only from then on. */
  isGroupLocked(group: RecruitGroupResponseDto | undefined | null): boolean {
    return this.recruitGroupService.isGroupLocked(group);
  }

  /** The briefs a group backs — what the Screening / Monitoring badges are derived from. */
  groupUsageBriefs(group: RecruitGroupResponseDto) {
    return this.recruitGroupService.groupUsageBriefs(group);
  }

  async removeCreatorFromGroup(creator: CreatorResponseDto): Promise<void> {
    const group = this.selectedGroup();
    if (!group) {
      return;
    }

    // Noodle has no remove-creator endpoint, so a synced group is add-only. Say why
    // rather than letting the request come back as a bare 409.
    if (this.isGroupLocked(group)) {
      await this.toastNotificationService.alert({
        title: 'This group is locked',
        message: GROUP_LOCKED_MESSAGE,
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: 'Remove from group?',
      message: `Remove ${creator.firstName} from "${group.groupName}"?`,
      type: 'confirm',
      icon: 'warning',
      confirmText: 'Remove',
      cancelText: 'Cancel',
    });
    if (!confirmed) {
      return;
    }

    try {
      await firstValueFrom(
        this.recruitGroupService.removeCreatorFromGroup(group.id, creator.blazeFirebaseUid).pipe(take(1)),
      );
      this.selectedGroupCreators.update((creators) =>
        (creators ?? []).filter((item) => item.blazeFirebaseUid !== creator.blazeFirebaseUid),
      );
      this.collaboratorsService.removeCreator(creator);
      this.recruitGroupService.notifyCreatorGroupMembershipChanged();
      await this.recruitGroupService.initRecruitmentGroups();
      const latest = this.groupList().find((item) => item.id === group.id);
      if (latest) {
        this.selectedGroup.set(latest);
      }
      await this.toastNotificationService.alert({
        title: 'Removed from group',
        message: `${creator.firstName} was removed from ${group.groupName}.`,
        type: 'success',
        icon: 'success',
      });
    } catch (error) {
      console.error('removeCreatorFromGroup() -> error', error);
      await this.toastNotificationService.alert({
        title: 'Remove failed',
        message: error instanceof Error && error.message === GROUP_LOCKED_MESSAGE
          ? GROUP_LOCKED_MESSAGE
          : 'Could not remove this creator. Please try again.',
        type: 'error',
        icon: 'error',
      });
    }
  }

  closeShareGroup(updated?: RecruitGroupResponseDto): void {
    this.showShareGroupModal.set(false);
    if (!updated) {
      return;
    }

    this.selectedGroup.set(updated);
    void this.recruitGroupService.initRecruitmentGroups().then(() => {
      const latest = this.groupList().find((group) => group.id === updated.id);
      this.selectedGroup.set({
        ...(latest ?? this.selectedGroup() ?? updated),
        allowReviewerInvite: updated.allowReviewerInvite,
        reviewerInviteBriefId: updated.reviewerInviteBriefId,
        groupDescription: updated.groupDescription,
      });
    });
  }

  openEditGroup(event: MouseEvent): void {
    event.stopPropagation();
    this.showEditGroupModal.set(true);
  }

  closeEditGroup(updated?: RecruitGroupResponseDto): void {
    this.showEditGroupModal.set(false);
    if (updated) {
      this.selectedGroup.set(updated);
      void this.recruitGroupService.initRecruitmentGroups();
    }
  }

  async deleteGroup(event: MouseEvent, group: RecruitGroupResponseDto): Promise<void> {
    event.stopPropagation();

    const confirmed = await this.alertService.confirm({
      title: 'Are you sure?',
      message: `Delete "${group.groupName}"?`,
      type: 'confirm',
      icon: 'warning',
      confirmText: 'Yes',
      cancelText: 'No',
    });

    if (!confirmed) {
      this.deleteGroupState.set('idle');
      return;
    }

    this.deleteGroupState.set('deleting');

    try {
      await firstValueFrom(this.recruitGroupService.removeGroupAndCreators(group.id).pipe(take(1)));
      this.selectedGroup.set(null);
      this.selectedGroupCreators.set(undefined);
      await this.recruitGroupService.initRecruitmentGroups();
      this.selectFirstGroupIfNoneSelected();
      this.deleteGroupState.set('idle');

      const successToast: ToastNotificationOptions = {
        title: 'Group removed',
        message: 'The group was deleted successfully.',
        type: 'success',
        icon: 'success',
      };
      await this.toastNotificationService.alert(successToast);
    } catch (error) {
      console.error('deleteGroup() -> error', error);
      const errorToast: ToastNotificationOptions = {
        title: 'Delete failed',
        message: 'Could not delete the group. Please try again.',
        type: 'error',
        icon: 'error',
      };
      await this.toastNotificationService.alert(errorToast);
      this.deleteGroupState.set('error');
      setTimeout(() => {
        this.deleteGroupState.set('idle');
      }, 0);
    }
  }
}

function groupAddedAt(group: RecruitGroupResponseDto): number {
  if (group.createdDate) {
    const fromCreatedDate = new Date(group.createdDate).getTime();
    if (!Number.isNaN(fromCreatedDate)) {
      return fromCreatedDate;
    }
  }

  if (/^[a-fA-F0-9]{24}$/.test(group.id)) {
    return Number.parseInt(group.id.slice(0, 8), 16) * 1000;
  }

  return 0;
}
